/**
 * Centralized measurement and nutrition unit helpers
 * Internal storage should use SI units: kg for weight, cm for height.
 */

export const KG_PER_LB = 0.45359237;
export const CM_PER_IN = 2.54;

export type WeightUnit = 'kg' | 'lbs';
export type HeightUnit = 'cm' | 'in';

export function convertWeight(value: number, from: WeightUnit, to: WeightUnit): number {
  if (!isFinite(value)) return 0;
  if (from === to) return value;
  return from === 'kg' ? value / KG_PER_LB : value * KG_PER_LB;
}

export function convertHeight(value: number, from: HeightUnit, to: HeightUnit): number {
  if (!isFinite(value)) return 0;
  if (from === to) return value;
  return from === 'cm' ? value / CM_PER_IN : value * CM_PER_IN;
}

export function clamp(min: number, v: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

// Plausibility ranges (can be refined later)
export function isPlausibleWeightKg(kg?: number | null): boolean {
  if (kg == null) return false;
  return kg >= 30 && kg <= 300;
}

export function isPlausibleHeightCm(cm?: number | null): boolean {
  if (cm == null) return false;
  return cm >= 100 && cm <= 250;
}

export function maybeLbsStoredAsKg(value?: number | null): boolean {
  if (value == null) return false;
  // If stored value is over typical human kg, likely lbs (e.g., 180 -> lbs)
  return value > 230; // 230 kg is extremely rare
}

export function maybeInchesStoredAsCm(value?: number | null): boolean {
  if (value == null) return false;
  // Heights like 60-80 likely inches (152-203 cm)
  return value >= 40 && value <= 90;
}

export interface NormalizedAnthroWarnings {
  warnings: string[];
}

export function normalizeProfileAnthropometrics<T extends {
  current_weight_kg?: number | null;
  height_cm?: number | null;
}>(p: T): { normalized: T; warnings: string[] } {
  const warnings: string[] = [];
  let weightKg = p.current_weight_kg ?? undefined;
  let heightCm = p.height_cm ?? undefined;

  // Detect and correct weight likely saved in lbs
  if (maybeLbsStoredAsKg(weightKg)) {
    weightKg = convertWeight(weightKg!, 'lbs', 'kg');
    warnings.push('Your weight looked like pounds but was saved as kilograms. We converted it to kg.');
  }

  // Detect and correct height likely saved in inches
  if (maybeInchesStoredAsCm(heightCm)) {
    heightCm = convertHeight(heightCm!, 'in', 'cm');
    warnings.push('Your height looked like inches but was saved as centimeters. We converted it to cm.');
  }

  // Final plausibility guardrails (do not auto-change, just warn)
  if (!isPlausibleWeightKg(weightKg)) {
    warnings.push('Your stored weight seems outside typical human range. Please review your profile.');
  }
  if (!isPlausibleHeightCm(heightCm)) {
    warnings.push('Your stored height seems outside typical human range. Please review your profile.');
  }

  const normalized = {
    ...p,
    current_weight_kg: weightKg as any,
    height_cm: heightCm as any,
  };

  return { normalized, warnings };
}

// Nutrition guardrails
export function clampDailyCalories(calories: number): number {
  // Reasonable bounds for general population
  return Math.round(clamp(800, calories, 4500));
}

export function capProteinByBodyWeightKg(proteinGrams: number, weightKg: number): number {
  // Cap protein at 2.2 g/kg baseline
  const maxProtein = Math.round(2.2 * weightKg);
  return Math.min(Math.max(0, Math.round(proteinGrams)), maxProtein);
}

export function rebalanceCarbFatToCalories(
  dailyCalories: number,
  proteinGrams: number,
  carbsGrams: number,
  fatsGrams: number
) {
  const proteinCals = proteinGrams * 4;
  const carbCals = carbsGrams * 4;
  const fatCals = fatsGrams * 9;
  const total = proteinCals + carbCals + fatCals;

  // If already within small tolerance, return as-is
  if (Math.abs(total - dailyCalories) <= 20) {
    return { proteinGrams, carbsGrams, fatsGrams };
  }

  // Keep protein fixed; rescale carbs+fats proportionally to fill remaining calories
  const remaining = Math.max(0, dailyCalories - proteinCals);
  const currentNonProtein = Math.max(1, carbCals + fatCals);
  const scale = remaining / currentNonProtein;

  const newCarbCals = Math.max(0, carbCals * scale);
  const newFatCals = Math.max(0, fatCals * scale);

  return {
    proteinGrams,
    carbsGrams: Math.round(newCarbCals / 4),
    fatsGrams: Math.round(newFatCals / 9),
  };
}
